import { z } from "zod";
import { ExerciseStructureSchema } from "./exerciseStructure.js";
import { CategoryHierarchySchema } from "./category.js";
import { CourseSchema, LevelSchema } from "./course.js";
import { ModuleSchema } from "./module.js";
// Schema for exercise translations (embedded in DynamoDB item)
export const ExerciseTranslationSchema = z.object({
    name: z.string().min(1),
    description: z.string().nullable().optional(),
    instructions: z.string().nullable().optional(),
});
// Exercise types supported
export const ExerciseTypeSchema = z.enum([
    "blanks",
    "gap",
    "multiple_choice",
    "sequence",
    "matching",
    "free_text",
    "pdf",
]);
// Main Exercise schema for DynamoDB
export const ExerciseSchema = z.object({
    id: z.number().int(),
    name: z.string().max(255),
    module_id: z.number().int(),
    difficulty: z.number().int().min(1).max(5).default(1),
    type: ExerciseTypeSchema.default("blanks"),
    is_published: z.boolean().default(false),
    requires_free_text_submission: z.boolean().default(false),
    estimated_duration_minutes: z.number().optional(),
    max_attempts: z.number().nullable().optional(),
    completion_threshold: z.number().optional(),
    order_index: z.number().optional(),
    slug: z.string().nullable().optional(),
    audio_url: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    instructions: z.string().nullable().optional(),
    requires_premium: z.boolean().default(false),
    created_at: z.date().optional(),
    updated_at: z.date().optional(),
    // Denormalized course info (DynamoDB - avoid joins)
    course_id: z.number().int().optional(),
    course_name: z.string().optional(),
    course_slug: z.string().optional(),
    // Denormalized module info (DynamoDB - avoid joins)
    module_name: z.string().optional(),
    module_slug: z.string().optional(),
    // Category IDs (stored as array, not relation table)
    category_ids: z.array(z.number().int()).optional(),
    // Embedded structure (DynamoDB - no separate table)
    structure: ExerciseStructureSchema.optional(),
    // Embedded translations (DynamoDB denormalization)
    translations: z.record(z.string(), ExerciseTranslationSchema).optional(),
});
// Exercise with full details
// Note: Server returns flat fields (course_id, course_name, module_name, category_ids)
// rather than nested objects. The nested fields are kept optional for backward compat.
export const ExerciseWithDetailsSchema = ExerciseSchema.extend({
    categories: z.array(CategoryHierarchySchema).optional(),
    course: CourseSchema.optional(),
    module: ModuleSchema.optional(),
});
// Exercise with details and structure (for exercise execution)
export const ExerciseWithDetailsAndStructureSchema = ExerciseWithDetailsSchema.extend({
    structure: ExerciseStructureSchema,
});
// Schema for creating/updating exercises
export const ExercisePayloadSchema = z.object({
    id: z.number().int().optional(),
    name: z.string().max(255),
    module_id: z.number().int(),
    difficulty: z.number().int().min(1).max(5).default(1),
    type: ExerciseTypeSchema.optional(),
    structure: ExerciseStructureSchema.optional(),
    category_ids: z.array(z.number().int()).optional(),
    is_published: z.boolean().optional(),
    requires_free_text_submission: z.boolean().optional(),
    estimated_duration_minutes: z.number().optional(),
    max_attempts: z.number().nullable().optional(),
    completion_threshold: z.number().optional(),
    order_index: z.number().optional(),
    slug: z.string().nullable().optional(),
    audio_url: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    instructions: z.string().nullable().optional(),
    requires_premium: z.boolean().optional(),
    // Denormalized info for create operations
    course_id: z.number().int().optional(),
    course_name: z.string().optional(),
    course_slug: z.string().optional(),
    module_name: z.string().optional(),
    module_slug: z.string().optional(),
    translations: z.record(z.string(), ExerciseTranslationSchema).optional(),
});
// Batch import schemas (for bulk upload endpoint)
export const BatchExerciseSchema = z.object({
    name: z.string().max(255),
    difficulty: z.number().int().min(1).max(5).default(1),
    estimated_duration_minutes: z.number().int().min(1).optional(),
    max_attempts: z.number().int().min(1).optional(),
    completion_threshold: z.number().int().min(0).max(100).default(80),
    order_index: z.number().int().min(0).default(0),
    slug: z.string().max(100).optional(),
    is_published: z.boolean().default(true),
    course: z.object({
        name: z.string().min(1),
        description: z.string().nullable().optional(),
        level: LevelSchema,
        target_language_code: z.string().max(5).default("de"),
        slug: z.string().max(100).optional(),
        price: z.number().min(0).default(0),
        estimated_duration_hours: z.number().int().min(1).optional(),
        order_index: z.number().int().min(0).default(0),
        thumbnail_url: z.string().url().optional(),
        is_published: z.boolean().default(true),
        translations: z
            .record(z.string(), z.object({
            name: z.string().min(1),
            description: z.string().nullable().optional(),
        }))
            .optional(),
    }),
    module: z.object({
        name: z.string().min(1),
        description: z.string().nullable().optional(),
        slug: z.string().max(100).optional(),
        video_link: z.string().url().optional(),
        order_index: z.number().int().min(0).default(0),
        estimated_duration_minutes: z.number().int().min(1).default(30),
        is_published: z.boolean().default(true),
        translations: z
            .record(z.string(), z.object({
            name: z.string().min(1),
            description: z.string().nullable().optional(),
        }))
            .optional(),
    }),
    categoryPaths: z.array(z.array(z.object({
        name: z.string().min(1),
        description: z.string().nullable().optional(),
        translations: z
            .record(z.string(), z.object({
            name: z.string().min(1),
            description: z.string().nullable().optional(),
        }))
            .optional(),
    }))),
    structure: z.object({
        content_type: z.string().default("json"),
    }),
    translations: z
        .record(z.string(), z.object({
        name: z.string().max(255),
        description: z.string().optional(),
    }))
        .optional(),
    structure_translations: z.record(z.string(), z.object({
        body: z.string().min(1),
        description: z.string().optional(),
        instructions: z.string().optional(),
    })),
});
export const BatchExercisePayloadSchema = z.object({
    exercises: z.array(BatchExerciseSchema),
});
